import { z } from "zod";
import { bedtimeCoverBriefV1Schema } from "./bedtime-publication.js";
const digest = z.string().regex(/^[a-f0-9]{64}$/u);
const identifier = z
    .string()
    .min(1)
    .max(240)
    .refine((value) => !value.includes("/") && !value.includes("\\"));
const context = {
    expectedBookRevision: z.number().int().nonnegative(),
    expectedPlanDigestSha256: digest,
};
/** Server-owned input for one native front slot, not a whole-wrap image request. */
export const bedtimeNativeCoverSourceSchema = z
    .object({
    contract: z.literal("BEDTIME_NATIVE_COVER_SOURCE_V1"),
    useId: z.literal("bedtime:canonical-cover-front"),
    planDigestSha256: digest,
    coverBriefDigestSha256: digest,
    coverBrief: bedtimeCoverBriefV1Schema,
    profileId: identifier,
    profileVersion: identifier,
    profileDigestSha256: digest,
    coverDesignPolicyVersion: z.literal("1.0.0"),
    coordinateSystem: z.literal("PDF_BOTTOM_LEFT"),
    destinationRectMilliPoints: z
        .object({
        xMilliPoints: z.number().int().nonnegative(),
        yMilliPoints: z.number().int().nonnegative(),
        widthMilliPoints: z.number().int().positive(),
        heightMilliPoints: z.number().int().positive(),
    })
        .strict(),
    minimumNativeSourcePixels: z
        .object({ width: z.number().int().positive(), height: z.number().int().positive() })
        .strict(),
    requiredGeneratedAssetKind: z.literal("SHOWCASE_COVER"),
    cropPolicy: z.literal("CENTERED_COVER_FILL"),
    truthBoundary: z.literal("ACCEPTED_PLAN_FRONT_SLOT_NOT_GENERATED_ART_OR_COVER_APPROVAL"),
    sourceDigestSha256: digest,
})
    .strict();
/** Identity-only commands; the server loads plans, immutable bytes and reviews. */
export const bedtimePublicationOutputCommandSchema = z.discriminatedUnion("action", [
    z
        .object({
        ...context,
        action: z.literal("REVIEW_EDITION"),
        proofJobId: identifier,
        expectedEditionReviewDigestSha256: digest,
        expectedCurrentEditionReviewDigestSha256: digest.nullable(),
        /**
         * How the buyer read the book before approving it. A guided buyer may read it on the page
         * (2026-10-14) without opening the exact print files; the approval then says so, and only
         * an approval made from the exact files can later be promoted toward print.
         */
        reviewedVia: z.enum(["EXACT_FILES", "IN_APP_READ_THROUGH"]).default("EXACT_FILES"),
        openedExactCover: z.boolean(),
        openedExactInterior: z.boolean(),
        confirmedCharacterDirection: z.literal(true),
        confirmedGuardianApplicability: z.literal(true),
        confirmedGeneratedLikeness: z.literal(true),
        confirmedCompleteSequence: z.literal(true),
        confirmedAccessibilityDescriptions: z.literal(true),
        confirmedExactDigitalEdition: z.boolean(),
    })
        .strict()
        .superRefine((command, context) => {
        const exact = command.openedExactCover &&
            command.openedExactInterior &&
            command.confirmedExactDigitalEdition;
        // An approval from the exact files says it opened both and confirmed them; nothing claims
        // a file was opened that wasn't.
        if (command.reviewedVia === "EXACT_FILES" && !exact)
            context.addIssue({
                code: "custom",
                path: ["reviewedVia"],
                message: "An approval from the exact print files must open and confirm both of them.",
            });
        if (command.confirmedExactDigitalEdition &&
            !(command.openedExactCover && command.openedExactInterior))
            context.addIssue({
                code: "custom",
                path: ["confirmedExactDigitalEdition"],
                message: "Only a buyer who opened both print files can confirm the exact digital edition.",
            });
    }),
    z
        .object({
        ...context,
        action: z.literal("BIND_MEDIA_USE"),
        useId: identifier,
        assetId: identifier,
        reviewId: identifier,
        expectedReviewDigestSha256: digest,
        expectedCurrentAuthorityDigestSha256: digest.nullable(),
    })
        .strict(),
    z
        .object({
        ...context,
        action: z.literal("PREPARE_COVER"),
        frontImageAssetId: identifier,
        frontImageDescription: z.string().trim().min(1).max(500),
    })
        .strict(),
    z
        .object({
        ...context,
        action: z.literal("APPROVE_COVER"),
        coverSourceId: identifier,
        expectedCoverChecksumSha256: digest,
        expectedCurrentCoverApprovalDigestSha256: digest.nullable(),
        /**
         * How the buyer saw the cover before keeping it. A guided buyer may keep it from its picture
         * on the page (2026-10-14) without opening the print file; the approval then says so. Print
         * still needs an edition review that opened the exact cover file.
         */
        reviewedVia: z.enum(["EXACT_FILE", "IN_APP_PREVIEW"]).default("EXACT_FILE"),
        confirmedExactCoverReview: z.boolean(),
    })
        .strict()
        .superRefine((command, context) => {
        // The approval claims the exact file was checked exactly when it was.
        if (command.confirmedExactCoverReview !== (command.reviewedVia === "EXACT_FILE"))
            context.addIssue({
                code: "custom",
                path: ["confirmedExactCoverReview"],
                message: "Only a cover kept from its exact print file confirms the exact cover; one kept from its picture on the page does not.",
            });
    }),
]);
export const bedtimePublicationOutputCommandResultSchema = z.discriminatedUnion("action", [
    z
        .object({
        action: z.literal("REVIEW_EDITION"),
        reviewId: identifier,
        editionReviewDigestSha256: digest,
        proofJobId: identifier,
        planDigestSha256: digest,
        approved: z.literal(true),
    })
        .strict(),
    z
        .object({
        action: z.literal("BIND_MEDIA_USE"),
        useId: identifier,
        assetId: identifier,
        planDigestSha256: digest,
        resolutionDecision: z.literal("PRINT_ELIGIBLE"),
    })
        .strict(),
    z
        .object({
        action: z.literal("PREPARE_COVER"),
        coverSourceId: identifier,
        checksumSha256: digest,
        objectVersionId: identifier,
        planDigestSha256: digest,
        nativeMinimumDpi: z.number().min(300),
    })
        .strict(),
    z
        .object({
        action: z.literal("APPROVE_COVER"),
        coverSourceId: identifier,
        checksumSha256: digest,
        planDigestSha256: digest,
        approved: z.literal(true),
    })
        .strict(),
]);
export const bedtimeCanonicalCoverSelectionSchema = z
    .object({
    bookId: identifier,
    bookRevision: z.number().int().nonnegative(),
    storyRevision: z.number().int().nonnegative(),
    sourceSnapshotDigestSha256: digest,
    planDigestSha256: digest,
    profileDigestSha256: digest,
    proofJobId: identifier,
    coverSourceId: identifier,
})
    .strict();
export const bedtimePublicationOutputRequirementsSchema = z
    .object({
    bookId: identifier,
    sourceRevision: z.number().int().nonnegative(),
    publicationPlanId: identifier,
    planDigestSha256: digest,
    profileId: identifier,
    profileDigestSha256: digest,
    mediaUses: z
        .array(z
        .object({
        useId: identifier,
        useDigestSha256: digest,
        nodeId: identifier,
        placementIds: z.array(identifier).min(1).max(96).optional(),
        nodeIds: z.array(identifier).min(1).max(96).optional(),
        minimumNativeCropPixels: z.object({
            width: z.number().int().positive(),
            height: z.number().int().positive(),
        }),
        currentAuthorityDigestSha256: digest.nullable(),
        isApproved: z.boolean(),
        assetId: identifier.nullable(),
    })
        .strict())
        .max(96),
    cover: z
        .object({
        title: z.string(),
        byline: z.string(),
        backCopy: z.string(),
        currentApprovalDigestSha256: digest.nullable(),
        coverSourceId: identifier.nullable(),
        isApproved: z.boolean(),
        nativeFrontArtSource: bedtimeNativeCoverSourceSchema.optional(),
        /** Whether the current cover was made with today's cover design; null with no cover. */
        designIsCurrent: z.boolean().nullable().optional(),
        /** The current cover's picture and its description, so it can be made again. */
        frontImageAssetId: identifier.nullable().optional(),
        frontImageDescription: z.string().trim().min(1).max(500).nullable().optional(),
        /** The credit and back words a cover prepared now prints, when they differ. */
        nextWording: z.object({ byline: z.string(), backCopy: z.string() }).strict().optional(),
    })
        .strict(),
    canApprove: z.boolean(),
    currentCanonicalCoverSelection: bedtimeCanonicalCoverSelectionSchema.nullable().optional(),
})
    .strict();
export const bedtimePublicationCoverReviewSchema = z
    .object({
    bookId: identifier,
    coverSourceId: identifier,
    planDigestSha256: digest,
    sourceRevision: z.number().int().nonnegative(),
    checksumSha256: digest,
    downloadUrl: z
        .string()
        .url()
        .refine((value) => value.startsWith("https://")),
    expiresIn: z.number().int().positive().max(300),
})
    .strict();
const privateEditionPdf = (pages) => z
    .object({
    assetId: identifier,
    objectVersionId: identifier,
    checksumSha256: digest,
    pageCount: z.literal(pages),
    contentType: z.literal("application/pdf").optional(),
    downloadUrl: z
        .string()
        .url()
        .refine((value) => value.startsWith("https://")),
})
    .strict();
/** Exact complete review copy; this is not an externally validated print package or an order. */
export const bedtimePublicationEditionReviewSchema = z
    .object({
    bookId: identifier,
    proofJobId: identifier,
    sourceRevision: z.number().int().nonnegative(),
    storyRevision: z.number().int().nonnegative().optional(),
    sourceSnapshotDigestSha256: digest.optional(),
    planDigestSha256: digest,
    outputAuthorityDigestSha256: digest,
    editionReviewDigestSha256: digest,
    currentEditionReviewDigestSha256: digest.nullable(),
    physicalProfile: z
        .object({
        profileId: identifier,
        profileVersion: identifier,
        profileDigestSha256: digest,
        coverMediaBoxPoints: z
            .object({ width: z.number().positive(), height: z.number().positive() })
            .strict(),
    })
        .strict()
        .optional(),
    coverPresentation: z
        .object({
        contract: z.literal("BEDTIME_CANONICAL_COVER_PRESENTATION_V1"),
        coverSourceId: identifier,
        coverApprovalDigestSha256: digest,
        fontPolicyDigestSha256: digest,
        frontImage: z
            .object({
            assetId: identifier,
            objectVersionId: identifier,
            checksumSha256: digest,
        })
            .strict(),
        presentationDigestSha256: digest,
        truthBoundary: z.literal("EXACT_PDF_DISPLAY_NOT_BROWSER_RECOMPOSITION_OR_PRINT_APPROVAL"),
    })
        .strict()
        .optional(),
    cover: privateEditionPdf(1),
    interior: privateEditionPdf(32),
    expiresIn: z.number().int().positive().max(300),
    title: z.string().min(1).max(500),
    characterNames: z.array(z.string().min(1).max(240)).max(20),
    guardianScope: z.enum([
        "NO_REFERENCE_PHOTOS",
        "ADULT_REFERENCES",
        "CHILD_REFERENCES",
        "ADULT_AND_CHILD_REFERENCES",
    ]),
    likenessScope: z.enum(["NO_REFERENCE_PHOTOS", "PERSON_REFERENCE_PHOTOS"]),
    coverDescription: z.string().min(1).max(500),
    readingTurns: z
        .array(z
        .object({
        turnId: identifier,
        sequence: z.number().int().positive().max(12),
        accessibleDescription: z.string().min(1).max(2000),
    })
        .strict())
        .length(12),
    canApprove: z.boolean(),
    isApproved: z.boolean(),
    truthBoundary: z.literal("COMPLETE_DIGITAL_EDITION_REVIEW_NOT_PRINT_PACKAGE_APPROVAL_OR_ORDER"),
})
    .strict();
//# sourceMappingURL=book-publication-output.js.map